import React, { useEffect, useRef, useState } from "react";
import { X as XIcon, Download, Share2, Loader2 } from "lucide-react";
import { LOGO_URL } from "../lib/constants";

/**
 * ShareCardModal — draws a shareable social image (PNG) of a match result
 * using the native Canvas API. No third-party dependency (html2canvas etc.).
 *
 * Props:
 *  - open (bool)
 *  - onClose ()
 *  - game { game_num, category, phase, day, time, ice, home_score, away_score, status }
 *  - home / away — team objects (may have .id, .has_logo, .logo_base64, .name, .color)
 *  - homeName / awayName — fallback names (placeholders when TBD)
 *  - phaseText — pretty phase label (already localized)
 *  - dateText — pretty date label (already localized)
 */
export default function ShareCardModal({
    open, onClose,
    game, home, away,
    homeName, awayName,
    phaseText, dateText,
}) {
    const canvasRef = useRef(null);
    const [rendering, setRendering] = useState(true);
    const [dataUrl, setDataUrl] = useState(null);
    const [sharing, setSharing] = useState(false);

    // Compute logo URLs — prefer inline base64 (admin) then binary endpoint (public).
    const backendBase = process.env.REACT_APP_BACKEND_URL || "";
    const homeLogoSrc = home?.logo_base64
        || (home?.has_logo && home?.id ? `${backendBase}/api/teams/${home.id}/logo` : null);
    const awayLogoSrc = away?.logo_base64
        || (away?.has_logo && away?.id ? `${backendBase}/api/teams/${away.id}/logo` : null);

    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        setRendering(true);
        setDataUrl(null);

        const load = (src) => new Promise((resolve) => {
            if (!src) return resolve(null);
            const img = new Image();
            img.crossOrigin = "anonymous";
            img.onload = () => resolve(img);
            img.onerror = () => resolve(null);
            img.src = src;
        });

        Promise.all([load(LOGO_URL), load(homeLogoSrc), load(awayLogoSrc)]).then(([tourneyImg, homeImg, awayImg]) => {
            if (cancelled) return;
            const canvas = canvasRef.current;
            if (!canvas) return;
            drawCard(canvas, {
                tourneyImg, homeImg, awayImg,
                game, home, away,
                homeName, awayName,
                phaseText, dateText,
            });
            try {
                setDataUrl(canvas.toDataURL("image/png"));
            } catch (e) {
                // Tainted canvas — should not happen with same-origin logos but keep safe.
                setDataUrl(null);
            }
            setRendering(false);
        });

        return () => { cancelled = true; };
    }, [open, game?.game_num, home?.id, away?.id, game?.home_score, game?.away_score]);

    if (!open) return null;

    const filename = `defi-ultime-${game?.game_num || "match"}-${(homeName || "").replace(/\s+/g, "_")}-vs-${(awayName || "").replace(/\s+/g, "_")}.png`;

    const download = () => {
        if (!dataUrl) return;
        const a = document.createElement("a");
        a.href = dataUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
    };

    const shareImage = async () => {
        if (!dataUrl) return;
        setSharing(true);
        try {
            const blob = await (await fetch(dataUrl)).blob();
            const file = new File([blob], filename, { type: "image/png" });
            if (navigator.canShare && navigator.canShare({ files: [file] })) {
                await navigator.share({
                    files: [file],
                    title: `${homeName} ${game.home_score} — ${game.away_score} ${awayName}`,
                    text: "Défi Ultime — Résultat de match",
                });
            } else {
                // Fallback: trigger download so user can attach manually.
                download();
            }
        } catch { /* user cancelled */ }
        setSharing(false);
    };

    return (
        <div
            className="fixed inset-0 bg-black/85 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={onClose}
            data-testid="share-card-modal"
        >
            <div onClick={e => e.stopPropagation()} className="bg-[#0a0a0a] border border-white/10 w-full max-w-lg max-h-[95vh] overflow-y-auto">
                <div className="p-4 border-b border-white/10 flex items-center justify-between">
                    <h2 className="font-display text-xl uppercase tracking-widest">Partager le résultat</h2>
                    <button onClick={onClose} className="text-white/60 hover:text-white" data-testid="btn-share-card-close">
                        <XIcon size={20} />
                    </button>
                </div>
                <div className="p-4">
                    <div className="relative bg-black/40 border border-white/5 aspect-[1/1] w-full overflow-hidden">
                        <canvas
                            ref={canvasRef}
                            width={1080}
                            height={1080}
                            className="w-full h-full block"
                            data-testid="share-card-canvas"
                        />
                        {rendering && (
                            <div className="absolute inset-0 flex items-center justify-center bg-black/60">
                                <Loader2 size={32} className="animate-spin text-[#4FE0D5]" />
                            </div>
                        )}
                    </div>
                    <p className="text-white/50 text-xs mt-3">
                        Format 1080×1080 optimisé pour Instagram, Facebook et Messenger.
                    </p>
                </div>
                <div className="p-4 pt-0 grid grid-cols-2 gap-2">
                    <button
                        onClick={download}
                        disabled={!dataUrl || rendering}
                        className="btn-primary flex items-center justify-center gap-2 disabled:opacity-50"
                        data-testid="btn-share-card-download"
                    >
                        <Download size={16} /> Télécharger
                    </button>
                    <button
                        onClick={shareImage}
                        disabled={!dataUrl || rendering || sharing}
                        className="btn-ghost flex items-center justify-center gap-2 disabled:opacity-50"
                        data-testid="btn-share-card-share"
                    >
                        {sharing ? <Loader2 size={16} className="animate-spin" /> : <Share2 size={16} />} Partager
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*   Canvas drawing — pure JS, no external deps                       */
/* ------------------------------------------------------------------ */
function drawCard(canvas, {
    tourneyImg, homeImg, awayImg,
    game, home, away,
    homeName, awayName,
    phaseText, dateText,
}) {
    const ctx = canvas.getContext("2d");
    const W = canvas.width;
    const H = canvas.height;

    // Background — dark radial gradient with subtle brand tint.
    const bg = ctx.createRadialGradient(W / 2, H * 0.35, 100, W / 2, H / 2, W * 0.85);
    bg.addColorStop(0, "#0f2a2b");
    bg.addColorStop(0.5, "#050b10");
    bg.addColorStop(1, "#000000");
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, W, H);

    // Diagonal accent stripes (very subtle)
    ctx.save();
    ctx.translate(W / 2, H / 2);
    ctx.rotate(-Math.PI / 8);
    ctx.globalAlpha = 0.04;
    ctx.fillStyle = "#7FD93C";
    ctx.fillRect(-W, -30, W * 2, 8);
    ctx.globalAlpha = 0.03;
    ctx.fillStyle = "#4FE0D5";
    ctx.fillRect(-W, 40, W * 2, 6);
    ctx.restore();

    // Header — tournament logo + "DÉFI ULTIME"
    if (tourneyImg) {
        const logoSize = 110;
        drawContainImage(ctx, tourneyImg, W / 2 - logoSize / 2, 55, logoSize, logoSize);
    }
    ctx.fillStyle = "#ffffff";
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.font = "700 34px 'Bebas Neue', 'Oswald', Impact, sans-serif";
    ctx.fillText("DÉFI ULTIME", W / 2, 180);
    ctx.fillStyle = "#4FE0D5";
    ctx.font = "600 20px 'Inter', system-ui, sans-serif";
    ctx.fillText("SOMMAIRE DE MATCH", W / 2, 224);

    // Divider
    ctx.strokeStyle = "rgba(255,255,255,0.12)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(120, 275);
    ctx.lineTo(W - 120, 275);
    ctx.stroke();

    // Category / Phase / Game # pill
    const pillY = 300;
    const pillLabel = [
        `PARTIE #${game.game_num}`,
        game.category,
        phaseText || "",
    ].filter(Boolean).join("  •  ");
    ctx.font = "700 22px 'Inter', system-ui, sans-serif";
    const pillWidth = ctx.measureText(pillLabel).width + 60;
    const pillX = W / 2 - pillWidth / 2;
    drawRoundRect(ctx, pillX, pillY, pillWidth, 48, 24);
    ctx.fillStyle = "rgba(79, 224, 213, 0.15)";
    ctx.fill();
    ctx.strokeStyle = "rgba(79, 224, 213, 0.4)";
    ctx.stroke();
    ctx.fillStyle = "#4FE0D5";
    ctx.textBaseline = "middle";
    ctx.fillText(pillLabel, W / 2, pillY + 24);

    // Score block — home | score | away
    const centerY = 560;
    const teamBoxW = 320;
    const logoBoxSize = 190;

    // Home column
    drawTeamColumn(ctx, {
        cx: 200 + teamBoxW / 2 - 100,
        cy: centerY,
        img: homeImg,
        color: home?.color || "#7FD93C",
        name: homeName,
        logoBoxSize,
        align: "center",
    });

    // Away column
    drawTeamColumn(ctx, {
        cx: W - (200 + teamBoxW / 2 - 100),
        cy: centerY,
        img: awayImg,
        color: away?.color || "#4FE0D5",
        name: awayName,
        logoBoxSize,
        align: "center",
    });

    // Score in the middle
    const winner = game.status === "final"
        ? (game.home_score > game.away_score ? "home" : game.home_score < game.away_score ? "away" : "tie")
        : null;

    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = winner === "home" ? "#7FD93C" : "#ffffff";
    ctx.font = "800 170px 'Bebas Neue', Impact, sans-serif";
    ctx.fillText(String(game.home_score ?? 0), W / 2 - 100, centerY);

    ctx.fillStyle = "rgba(255,255,255,0.25)";
    ctx.font = "500 90px 'Bebas Neue', Impact, sans-serif";
    ctx.fillText("—", W / 2, centerY);

    ctx.fillStyle = winner === "away" ? "#7FD93C" : "#ffffff";
    ctx.font = "800 170px 'Bebas Neue', Impact, sans-serif";
    ctx.fillText(String(game.away_score ?? 0), W / 2 + 100, centerY);

    // Winner banner
    if (winner === "home" || winner === "away") {
        const winnerName = winner === "home" ? homeName : awayName;
        const bannerY = 780;
        ctx.fillStyle = "#7FD93C";
        drawRoundRect(ctx, W / 2 - 260, bannerY, 520, 60, 30);
        ctx.fill();
        ctx.fillStyle = "#000000";
        ctx.font = "800 24px 'Inter', system-ui, sans-serif";
        ctx.textBaseline = "middle";
        ctx.fillText(`VAINQUEUR : ${winnerName.toUpperCase()}`, W / 2, bannerY + 30);
    } else if (winner === "tie") {
        ctx.fillStyle = "rgba(255,255,255,0.15)";
        drawRoundRect(ctx, W / 2 - 120, 780, 240, 60, 30);
        ctx.fill();
        ctx.fillStyle = "#ffffff";
        ctx.font = "800 24px 'Inter', system-ui, sans-serif";
        ctx.textBaseline = "middle";
        ctx.fillText("MATCH NUL", W / 2, 810);
    } else {
        ctx.fillStyle = "rgba(79, 224, 213, 0.2)";
        drawRoundRect(ctx, W / 2 - 120, 780, 240, 60, 30);
        ctx.fill();
        ctx.fillStyle = "#4FE0D5";
        ctx.font = "800 22px 'Inter', system-ui, sans-serif";
        ctx.textBaseline = "middle";
        ctx.fillText("À VENIR", W / 2, 810);
    }

    // Footer — date + arena + URL
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillStyle = "#ffffff";
    ctx.font = "600 26px 'Inter', system-ui, sans-serif";
    const dateLine = [dateText, game.time, game.ice].filter(Boolean).join("  •  ");
    ctx.fillText(dateLine, W / 2, 890);

    ctx.fillStyle = "rgba(255,255,255,0.45)";
    ctx.font = "500 20px 'Inter', system-ui, sans-serif";
    ctx.fillText("defiultime.com", W / 2, 940);

    // Bottom brand bar
    ctx.fillStyle = "#7FD93C";
    ctx.fillRect(0, H - 12, W / 2, 12);
    ctx.fillStyle = "#4FE0D5";
    ctx.fillRect(W / 2, H - 12, W / 2, 12);
}

function drawTeamColumn(ctx, { cx, cy, img, color, name, logoBoxSize }) {
    // Logo box background
    const boxX = cx - logoBoxSize / 2;
    const boxY = cy - logoBoxSize / 2 - 20;
    ctx.save();
    ctx.fillStyle = "rgba(255,255,255,0.04)";
    drawRoundRect(ctx, boxX, boxY, logoBoxSize, logoBoxSize, 16);
    ctx.fill();
    ctx.strokeStyle = "rgba(255,255,255,0.08)";
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.restore();

    if (img) {
        drawContainImage(ctx, img, boxX + 15, boxY + 15, logoBoxSize - 30, logoBoxSize - 30);
    } else {
        // Fallback: initials tile
        ctx.save();
        ctx.fillStyle = color;
        ctx.globalAlpha = 0.15;
        drawRoundRect(ctx, boxX + 15, boxY + 15, logoBoxSize - 30, logoBoxSize - 30, 8);
        ctx.fill();
        ctx.globalAlpha = 1;
        ctx.fillStyle = color;
        ctx.font = "800 70px 'Bebas Neue', Impact, sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        const initials = (name || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();
        ctx.fillText(initials, cx, boxY + logoBoxSize / 2);
        ctx.restore();
    }

    // Team name below
    ctx.fillStyle = "#ffffff";
    ctx.font = "700 22px 'Inter', system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    const nameY = boxY + logoBoxSize + 16;
    const label = truncateToWidth(ctx, (name || "TBD").toUpperCase(), logoBoxSize + 40);
    ctx.fillText(label, cx, nameY);
}

function drawContainImage(ctx, img, x, y, w, h) {
    const iw = img.naturalWidth || img.width;
    const ih = img.naturalHeight || img.height;
    if (!iw || !ih) return;
    const ratio = Math.min(w / iw, h / ih);
    const dw = iw * ratio;
    const dh = ih * ratio;
    const dx = x + (w - dw) / 2;
    const dy = y + (h - dh) / 2;
    ctx.drawImage(img, dx, dy, dw, dh);
}

function drawRoundRect(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
}

function truncateToWidth(ctx, str, maxWidth) {
    if (ctx.measureText(str).width <= maxWidth) return str;
    let s = str;
    while (s.length > 0 && ctx.measureText(s + "…").width > maxWidth) {
        s = s.slice(0, -1);
    }
    return s + "…";
}
